import { Location } from "@angular/common";
import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntil } from "rxjs/operators";

import { BaseComponent, UserService, ProductService, SimpleProduct, AdminService, WindowService, EventBusService, HelperService, environment } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { SkuFoundConfirmComponent } from "../../modals";
import { format } from "date-fns";
import { TranslateService } from "@ngx-translate/core";
import { fenderMarketLabel } from "./fender-market-label";

type FenderQueryType = "sku" | "serial" | "productNo" | "bodyType" | "series" | "title" | "taxonomy";
type FenderProductSearchType = "sku" | "productNo" | "bodyType" | "series" | "title" | "taxonomy";
type MissingSerialQueryType = "email" | "userId" | "keyword";

interface FenderProductRow {
  sku: string;
  title: string;
  imageUrl: string;
  productNo: string;
  brand: string;
  series: string;
  bodyType: string;
  color: string;
  taxonomy: string;
  subData: string[];
  href: string;
  marketLabel: string;
  metadata: { title: string; value: string }[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-admin-fender-products",
  templateUrl: "fender-products.component.html",
  standalone: false,
})
export class AdminFenderProductsComponent extends BaseComponent implements OnInit {
  public loading: boolean;
  public products: Array<SimpleProduct>;
  public displayedProducts: Array<SimpleProduct>;
  public filteredProducts: Array<SimpleProduct>;
  public showMigrated: boolean = false;
  public sections: any = [
    {
      name: "Fender Products",
      id: "list",
      selected: true,
    },
    {
      name: "Missing Serials",
      id: "missing_serials",
    },
  ];
  public selectedSection;
  public pageOffset = 1;
  public pageLimit = 100;
  public totalPages: number;
  public missingSerials: Array<any>;
  public displayedMissingSerials: Array<any>;
  public nextToken: string | null = null;
  public previousTokens: string[] = [];
  public loadingNext: boolean = false;
  public loadingPrevious: boolean = false;
  private pageCache: { [key: string]: { items: any[]; nextToken: string | null } } = {};
  // Product-name keyword the paginated listing is filtered by server-side. Kept apart from
  // queryValue so paging keeps the keyword that was searched, not whatever is typed since.
  public missingSerialKeyword: string = "";
  public metadataMap: { [key: string]: any[] } = {};
  public filters: {
    migrated: "yes" | "no" | "any";
    sku: "yes" | "no" | "any";
  } = {
    migrated: "no",
    sku: "any",
  };

  // Combined query type: SKU/Serial are single-result lookups that jump to the detail page.
  // productNo/bodyType/series/title are multi-result searches that render a myp-list-item list.
  public queryType: FenderQueryType | MissingSerialQueryType = "sku";
  public queryValue: string = "";
  public searching: boolean = false;
  public savingSku: boolean = false;
  // Pre-computed template flags, kept in sync with queryType via onQueryTypeChanged() —
  // avoids method calls in bindings.
  public isDetailLookup: boolean = true;
  public isProductSearchType: boolean = false;
  // Taxonomy is exact-match only, so it gets a <select> of taxonomyOptions instead of the
  // free-text input every other search type uses. Fetched from paeapi (backed by SSM — see
  // fenderProducts.getFmicTaxonomies()) in ngOnInit rather than hardcoded here.
  public isTaxonomySearch: boolean = false;
  public queryPlaceholder: string = "Enter sku...";
  public taxonomyOptions: readonly string[] = [];

  // Multi-result search state (productNo/bodyType/series/title).
  public fenderProductResults: FenderProductRow[] = [];
  public fenderProductResultsNextToken: string | null = null;
  public fenderProductResultsPageSize: number = 50;
  public loadingMore: boolean = false;

  // Dry-run mirrors the detail page: when the FMIC flag is off, admins can opt into the new
  // API on a per-search basis. Forwarded to the detail route as a query param.
  public dryRunFmic: boolean = false;
  public fmicProductsApiEnabled: boolean = !!(environment as any).features?.useFmicProductsApi;

  constructor(
    userService: UserService,
    public productService: ProductService,
    public adminService: AdminService,
    public appService: AppService,
    private _activatedRoute: ActivatedRoute,
    public router: Router,
    public location: Location,
    private _win: WindowService,
    private _eventbusService: EventBusService,
    private _translate: TranslateService,
    public helperService: HelperService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.loading = true;
    this.productService
      .getFmicTaxonomies()
      .pipe(takeUntil(this.destroy$))
      .subscribe((taxonomies: string[]) => (this.taxonomyOptions = taxonomies));
    const queryParams = this._activatedRoute.snapshot.queryParams;
    const sectionFromRoute = this._activatedRoute.snapshot.params["type"]?.toLowerCase();
    const sectionId = sectionFromRoute === "missing_serials" ? "missing_serials" : "list";
    // Skip the navigate — the URL already reflects `sectionId` (that's how we read it),
    // and calling router.navigate here would replace it with a param-less URL, wiping
    // `type` / `value` off the address bar and breaking cache restoration on back-nav.
    this.selectSection(sectionId, false);

    const queryType = queryParams["type"] as FenderQueryType | undefined;
    const queryValue = queryParams["value"];
    if (sectionId === "list" && queryType && queryValue?.trim()) {
      // Restore the search bar so the URL round-trips, but never auto-run — the user has
      // to submit to trigger a network call. This prevents back-nav from re-searching and
      // auto-bouncing you back to the detail page when a query has a single result.
      this.queryType = queryType;
      this.queryValue = queryValue.trim();
      this.onQueryTypeChanged();

      // If the last search in the cache matches these query params, paint its results
      // right away so the list looks unchanged after a back-nav.
      const cached = this.adminService.fenderProductSearchCache;
      if (cached && cached.queryType === this.queryType && cached.queryValue === this.queryValue && cached.dryRunFmic === this.dryRunFmic && this.isProductSearchType) {
        this.fenderProductResults = cached.items.map((item: any) => this.toRow(item));
        this.fenderProductResultsNextToken = cached.nextToken;
        this.fenderProductResultsPageSize = cached.pageSize;
        // Restore the scroll position saved when the user clicked into a detail row. The
        // parent products-page owns #contentContainer; wait for change detection to lay
        // out the rows before scrolling, otherwise scrollTop clamps to 0.
        const savedScroll = this.appService.scrolledContentOffsets.admin_fender_products;
        if (savedScroll) {
          this._win.setTimeout(() => {
            const container = document.getElementById("contentContainer");
            if (container) container.scrollTop = savedScroll;
          }, 200);
        }
      } else if (queryParams["run"] === "1" && this.isProductSearchType) {
        // Explicit cross-link (e.g. the "Product Number" chip on the detail page). Fire
        // the search immediately, then strip `run` from the URL so a subsequent back-nav
        // doesn't re-trigger it.
        this.runFenderSearch();
        this.router.navigate([], {
          relativeTo: this._activatedRoute,
          queryParams: { type: this.queryType, value: this.queryValue },
          replaceUrl: true,
        });
      } else if (this.isProductSearchType) {
        // Cache miss on a URL that carries a valid product search (bookmark, deep link,
        // or back-nav after a detail-page refresh cleared the in-memory cache). Rehydrate
        // by re-running the search silently. Auto-nav-on-single is disabled so a SKU URL
        // doesn't bounce you back to the detail page you just navigated away from.
        this.searchFenderProducts(this.queryValue, false, undefined, { autoNavigateOnSingle: false });
      }
    }
  }

  onQueryTypeChanged() {
    // Only Serial is a pure detail-lookup — SKU/productNo/bodyType/series/title/taxonomy all
    // go through the search endpoint and auto-navigate on a single hit.
    this.isDetailLookup = this.queryType === "serial";
    this.isProductSearchType =
      this.queryType === "sku" || this.queryType === "productNo" || this.queryType === "bodyType" || this.queryType === "series" || this.queryType === "title" || this.queryType === "taxonomy";
    this.isTaxonomySearch = this.queryType === "taxonomy";
    this.queryPlaceholder = this.computePlaceholder();
  }

  runFenderSearch() {
    const trimmed = this.queryValue?.trim();
    if (!trimmed) return;

    // Fresh submit — discard any saved scroll position from a prior back-nav so the
    // new results render at the top instead of jumping into stale offsets.
    this.appService.scrolledContentOffsets.admin_fender_products = 0;

    this.router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: { type: this.queryType, value: trimmed },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });

    if (this.isDetailLookup) {
      this.searchFenderGear(trimmed);
    } else if (this.isProductSearchType) {
      this.searchFenderProducts(trimmed);
    }
  }

  // Serial numbers aren't queryable via the FMIC products search endpoint, so they still
  // go through the gear-detail endpoint (which does serial → SKU resolution server-side)
  // and then jump straight to the detail page on success.
  searchFenderGear(value: string) {
    this.searching = true;
    // Serials aren't routable — resolve serial → sku here (the detail page's route is
    // keyed by sku), then navigate. The detail page does its own fetch-or-build on load;
    // it doesn't trust anything handed to it from here.
    this.adminService.searchFenderGear(undefined, value, this.dryRunFmic).subscribe({
      next: (result: any) => {
        const snapshot = Array.isArray(result?.snapshot) ? result.snapshot[0] : result?.snapshot;
        const sku = snapshot?.product?.fmic_material_number || snapshot?.product?.stringId;
        this.searching = false;
        if (sku) {
          this.router.navigate(["/products/fender_products/detail", sku], {
            queryParams: this.dryRunFmic ? { dryRunFmic: "true" } : undefined,
          });
        } else {
          this.appService.alert("No Fender gear found with that serial number.");
        }
      },
      error: () => {
        this.searching = false;
        this.appService.alert("No Fender gear found with that serial number.");
      },
    });
  }

  searchFenderProducts(value: string, appendPage: boolean = false, nextToken?: string, options: { autoNavigateOnSingle?: boolean } = {}) {
    const autoNavigateOnSingle = options.autoNavigateOnSingle !== false;
    if (!appendPage) {
      this.fenderProductResults = [];
      this.fenderProductResultsNextToken = null;
      this.searching = true;
    } else {
      this.loadingMore = true;
    }
    const params: any = { pageSize: this.fenderProductResultsPageSize };
    params[this.queryType as FenderProductSearchType] = value;
    if (nextToken) params.nextToken = nextToken;

    this.adminService.searchFenderProducts(params).subscribe({
      next: (result: any) => {
        const items: any[] = result?.items || [];
        const rows: FenderProductRow[] = items.map((item: any) => this.toRow(item));
        this.fenderProductResults = appendPage ? [...this.fenderProductResults, ...rows] : rows;
        this.fenderProductResultsNextToken = result?.pagination?.nextToken || null;
        this.searching = false;
        this.loadingMore = false;

        // Cache the raw items so a back-nav from the detail page can restore the exact
        // result set without re-hitting the API. Store the raw API payload rather than
        // the display rows so URL/dryRun formatting stays in sync when we rebuild rows.
        const rawItems = appendPage ? [...(this.adminService.fenderProductSearchCache?.items || []), ...items] : items;
        this.adminService.fenderProductSearchCache = {
          queryType: this.queryType,
          queryValue: value,
          dryRunFmic: this.dryRunFmic,
          items: rawItems,
          nextToken: this.fenderProductResultsNextToken,
          pageSize: this.fenderProductResultsPageSize,
        };

        // Auto-navigate to detail when the query resolves to exactly one product and there
        // are no additional pages. Only fires on a fresh, user-initiated search (not on
        // Load More and not on a URL-triggered re-hydrate). Otherwise a SKU URL would
        // bounce right back to the detail page after a refresh.
        if (autoNavigateOnSingle && !appendPage && rows.length === 1 && !this.fenderProductResultsNextToken) {
          this.onProductRowClicked(rows[0]!);
        }
      },
      error: () => {
        this.searching = false;
        this.loadingMore = false;
        this.appService.alert("Fender product search failed. Please try again or narrow your query.");
      },
    });
  }

  loadMoreFenderProducts() {
    if (!this.fenderProductResultsNextToken || this.loadingMore) return;
    this.searchFenderProducts(this.queryValue.trim(), true, this.fenderProductResultsNextToken);
  }

  private toRow(item: any): FenderProductRow {
    const subData: string[] = [];
    if (item.series) subData.push(item.series);
    if (item.bodyType) subData.push(item.bodyType);
    if (item.color) subData.push(item.color);
    // Confirms the match for a taxonomy search specifically — series/bodyType/color are more
    // relevant for the other search types, so this doesn't show there too.
    if (this.isTaxonomySearch && item.taxonomy) subData.push(item.taxonomy);
    if (item.productNo && !subData.length) subData.push(`Product #${item.productNo}`);

    const sku = item.sku || "";
    const marketLabel = fenderMarketLabel(item.title, item.skuDisplayName);
    const metadata: { title: string; value: string }[] = [{ title: "SKU", value: sku }];
    if (marketLabel) metadata.push({ title: "Variant", value: marketLabel });

    return {
      sku,
      title: item.title || item.sku || "Unknown product",
      imageUrl: item.imageUrl || "",
      productNo: item.productNo || "",
      brand: item.brand || "",
      series: item.series || "",
      bodyType: item.bodyType || "",
      color: item.color || "",
      taxonomy: item.taxonomy || "",
      subData,
      href: `/products/fender_products/detail/${item.sku}${this.dryRunFmic ? "?dryRunFmic=true" : ""}`,
      marketLabel,
      metadata,
    };
  }

  onProductRowClicked(row: FenderProductRow) {
    if (!row?.sku) return;
    // Remember scroll position so back-nav restores the same view of the results list.
    const container = document.getElementById("contentContainer");
    if (container) this.appService.scrolledContentOffsets.admin_fender_products = container.scrollTop;
    // The list only ever holds a lean, unpersisted search preview — the detail page always
    // fetches-or-builds the real snapshot itself, so there's nothing to hand off here.
    this.router.navigate(["/products/fender_products/detail", row.sku], {
      queryParams: this.dryRunFmic ? { dryRunFmic: "true" } : undefined,
    });
  }

  private computePlaceholder(): string {
    switch (this.queryType) {
      case "sku":
        return "Enter sku...";
      case "serial":
        return "Enter a serial number...";
      case "productNo":
        return "Enter product number...";
      case "bodyType":
        return "e.g. Stratocaster, Telecaster, Jazz Bass...";
      case "series":
        return "e.g. Player, American Ultra...";
      case "title":
        return "Free-text search on product name...";
      case "taxonomy":
        return "Select a taxonomy...";
      case "email":
        return "Enter email...";
      case "userId":
        return "Enter a user ID...";
      case "keyword":
        return "Search product names...";
      default:
        return "";
    }
  }

  search() {
    this.missingSerialKeyword = this.queryType == "keyword" ? this.queryValue.trim() : "";
    if (this.queryType == "keyword" || this.queryValue.trim() == "") {
      this.loadMissingSerials(null, true);
      return;
    }
    this.searching = true;
    let args: any = {};
    if (this.queryType == "email") {
      args.email = this.queryValue;
    } else if (this.queryType == "userId") {
      args.userId = this.queryValue;
    }
    this.adminService.put(`product/fender-gear/search-missing-serials`, args).subscribe({
      next: (result: any) => {
        if (result?.length) {
          this.missingSerials = result;
          this.displayedMissingSerials = [...this.missingSerials];
        } else {
          this.appService.alert("Either that user was not found or they have not entered any missing serials.");
        }
      },
      error: (error) => {
        this.searching = false;
        this.savingSku = false;
        this.appService.alert("Either that user was not found or they have not entered any missing serials.");
      },
      complete: () => {
        this.searching = false;
        this.savingSku = false;
      },
    });
  }

  showImage(url) {
    this._eventbusService.emit(this._eventbusService.types.viewFullscreenImage, url);
  }

  saveSku(ms) {
    this.savingSku = true;
    this.adminService
      .put(`product/fender-gear/missing-serials`, {
        sku: ms.sku,
        serial: ms.userId + "#" + ms.serial,
      })
      .subscribe({
        next: (result: any) => {
          this.savingSku = false;
          ms.showingEnterSku = false;
          this.displayedMissingSerials = this.displayedMissingSerials.map((item) => (item.serial == ms.serial ? result : item));
          this.missingSerials = this.missingSerials.map((item) => (item.serial == ms.serial ? result : item));
        },
        error: (error) => {
          this.savingSku = false;
          this.appService.alert("We didn't find a Fender product with that SKU.");
        },
        complete: () => {
          this.savingSku = false;
        },
      });
  }

  handleSerialUpdated(ms: any) {
    this.missingSerials = this.missingSerials.map((item) => (item.serial === ms.serial ? ms : item));
    this.displayedMissingSerials = this.displayedMissingSerials.map((item) => (item.serial === ms.serial ? ms : item));
  }

  handleConverted(ms: any) {
    this.missingSerials = this.missingSerials.filter((item) => item !== ms);
    this.displayedMissingSerials = this.displayedMissingSerials.filter((item) => item !== ms);
  }

  deleteMissingSerial(ms) {
    this.appService.confirm("Are you sure you want to remove this gear from this account? This cannot be undone, and the product will be removed from the customer's account.").then((result) => {
      if (result) {
        this.adminService
          .put(`product/fender-gear/delete-missing-serial`, {
            serial: ms.serial,
            userId: ms.userId,
          })
          .subscribe((result) => {
            // remove the item from the list
            this.missingSerials = this.missingSerials.filter((item) => item.serial != ms.serial);
            this.displayedMissingSerials = this.displayedMissingSerials.filter((item) => item.serial != ms.serial);
          });
      }
    });
  }

  public converting: boolean = false;
  public selectedSku: string = "";
  convertMissingSerial(ms) {
    const msg = this._translate.instant("products.register.missing_fender_serial.convert_confirmation") + this._translate.instant("products.register.missing_fender_serial.convert_needs_sku");
    this.appService.prompt(msg, undefined, undefined, undefined, ms.sku || "").then((result) => {
      if (result) {
        this.converting = true;
        this.selectedSku = String(result).trim();
        this.adminService.put("product/fender-gear/missing-serials/convert", { serial: ms.serial, sku: this.selectedSku, userId: ms.userId }).subscribe({
          next: (result: any) => {
            console.log(result);
            this.converting = false;
            if (result.product) {
              const modalRef = this.appService.showModal(SkuFoundConfirmComponent, { size: "md" });
              modalRef.componentInstance.imageUrl = result.product.imageUrl;
              modalRef.componentInstance.productTitle = result.product.title;
              modalRef.componentInstance.sku = this.selectedSku;
              modalRef.componentInstance.submitFn = () => {
                this.confirmConvertMissingSerial(ms);
              };
            }
          },
          error: (error) => {
            this.appService.alertError(error);
            this.converting = false;
          },
          complete: () => {
            this.converting = false;
          },
        });
      }
    });
  }

  confirmConvertMissingSerial(ms) {
    this.converting = true;
    this.adminService.put("product/fender-gear/missing-serials/convert", { serial: ms.serial, sku: this.selectedSku, userId: ms.userId, confirmed: true }).subscribe({
      next: (result: any) => {
        this.converting = false;
        this.appService.alert(this._translate.instant("products.register.missing_fender_serial.convert_success"));
        this.missingSerials = this.missingSerials.filter((item) => item.serial != ms.serial);
        this.displayedMissingSerials = this.displayedMissingSerials.filter((item) => item.serial != ms.serial);
      },
    });
  }

  selectSection(id, navigate = true) {
    this.selectedSection = id;

    this.sections.forEach((section) => {
      section.selected = false;
      if (section.id == id) section.selected = true;
    });
    this.pageOffset = 1;
    this.nextToken = null;
    this.previousTokens = [];
    this.pageCache = {};
    if (navigate) this.router.navigate(["/products/fender_products", id.toLowerCase()], { replaceUrl: true });
    if (id == "list") {
      this.queryType = "sku";
      this.queryValue = "";
      this.fenderProductResults = [];
      this.fenderProductResultsNextToken = null;
      this.onQueryTypeChanged();
    } else if (id == "missing_serials") {
      this.queryType = "email";
      this.queryValue = "";
      this.missingSerialKeyword = "";
      this.isDetailLookup = false;
      this.isProductSearchType = false;
      this.loadMissingSerials();
    }
  }

  loadMissingSerials(token?: string | null, changingPageLimit: boolean = false) {
    // Create cache key - use 'first' for null token, otherwise use the token itself
    if (changingPageLimit) {
      this.pageOffset = 1;
      this.nextToken = null;
      this.previousTokens = [];
      this.loading = true;
      this.pageCache = {};
    }
    const cacheKey = token || "first";
    // Check if we have this page cached
    if (this.pageCache[cacheKey]) {
      const cached = this.pageCache[cacheKey];
      this.missingSerials = cached.items;
      this.nextToken = cached.nextToken;
      this.previousTokens[this.pageOffset] = token || null;
      // Reset loading flags immediately for cached pages
      this.loadingNext = false;
      this.loadingPrevious = false;
      this.filter();
      return;
    }

    // Not in cache, fetch from API
    let url = "product/fender-gear/missing-serials";
    const params: string[] = [];

    params.push(`limit=${this.pageLimit}`);
    params.push(`sortDirection=desc`);
    if (token) {
      params.push(`nextToken=${token}`);
    }
    if (this.missingSerialKeyword) {
      params.push(`keyword=${encodeURIComponent(this.missingSerialKeyword)}`);
    }

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    this.adminService.get(url).subscribe((result: any) => {
      this.loading = false;
      this.loadingNext = false;
      this.loadingPrevious = false;

      if (result && result.items) {
        this.missingSerials = result.items;
        this.nextToken = result.nextToken || null;
        // Store the token that was used to get to this page
        // For page 1, this will be null
        this.previousTokens[this.pageOffset] = token || null;
        // Cache the result
        this.pageCache[cacheKey] = {
          items: [...result.items],
          nextToken: this.nextToken,
        };
        // Create metadata map
        result.items.forEach((item) => {
          this.metadataMap[item.userId + "-" + item.serial] = [
            { title: "Date Added", value: item.created ? format(new Date(item.created), "MM/dd/yyyy") : "Unknown" },
            { title: "Serial Number", value: item.serial },
            { title: "SKU", value: item.sku || "No SKU entered.", valueColor: item.sku ? "green" : "red" },
          ];
          if (item.migrated) {
            this.metadataMap[item.userId + "-" + item.serial].push({ title: "", value: "Migrated" });
          }
        });
        this.filter();
      } else {
        // Fallback for old API format
        this.missingSerials = Array.isArray(result) ? result : [];
        this.nextToken = null;
        this.previousTokens[this.pageOffset] = token || null;
        // Cache the result
        this.pageCache[cacheKey] = {
          items: [...this.missingSerials],
          nextToken: null,
        };
        this.filter();
      }
    });
  }

  paginate = (page: string) => {
    // Always reset both loading flags first to clear any previous state
    this.loadingNext = false;
    this.loadingPrevious = false;

    if (page === "next") {
      if (this.nextToken) {
        // Check if we have this page cached
        this.loadingNext = this.pageCache[this.nextToken] ? false : true;
        // Save the token we're about to use for the next page
        const tokenToUse = this.nextToken;
        this.pageOffset++;
        this.previousTokens[this.pageOffset] = tokenToUse;
        this.loadMissingSerials(tokenToUse);
      }
    } else if (page === "previous") {
      if (this.pageOffset > 1) {
        // Check if we have this page cached
        // Get the token that was used to get to the current page
        const tokenForPreviousPage = this.previousTokens[this.pageOffset - 1] || null;
        this.loadingPrevious = this.pageCache[tokenForPreviousPage] ? false : true;
        this.pageOffset--;
        this.loadMissingSerials(tokenForPreviousPage);
      }
    }
  };

  toggleMigrated() {
    this.showMigrated = !this.showMigrated;
    this.filter();
  }

  filter() {
    let filtered = [...this.missingSerials];
    if (this.filters.migrated == "yes") {
      filtered = [...filtered.filter((ms) => ms.migrated)];
    } else if (this.filters.migrated == "no") {
      filtered = [...filtered.filter((ms) => !ms.migrated)];
    } else {
      filtered = [...filtered];
    }
    if (this.filters.sku == "yes") {
      filtered = [...filtered.filter((ms) => ms.sku)];
    } else if (this.filters.sku == "no") {
      filtered = [...filtered.filter((ms) => !ms.sku)];
    }
    this.displayedMissingSerials = [...filtered];
  }
}
